/**
 * collision.ts
 * AABB collision detection and platform resolution for Starfall Courier.
 *
 * All functions are pure — no React imports, no async, no side effects.
 * Called each fixed-step tick from gameLoop.ts.
 *
 * Phase 1 — Movement Prototype.
 * See ROADMAP.md Phase 1 and ADR-002.
 *
 * @module collision
 */

import type { AABB } from '../types/entity';
import { LEDGE_FORGIVENESS, GRAVITY, FIXED_STEP } from './physics';

/**
 * Maximum vertical penetration expected when Nova is grounded (one frame of gravity).
 * If an entity overlaps a platform from above by ≤ this amount, the collision is
 * always resolved vertically — preventing adjacent same-height platforms from
 * acting as walls at their shared seam.
 */
// DECISION: GROUND_SKIN = 4× one-frame gravity penetration (GRAVITY × dt²).
// The actual per-frame sink is ~0.5px; multiplying by 4 yields 2px which covers
// float drift and sub-pixel accumulation across multiple ticks.
// Exported so physicsStep.ts can nudge past this zone when ground support fails.
export const GROUND_SKIN = Math.ceil(GRAVITY * FIXED_STEP * FIXED_STEP * 4);

/** Result of resolving a single platform collision. */
export interface CollisionResult {
  /** Corrected X position after resolution. */
  x: number;
  /** Corrected Y position after resolution. */
  y: number;
  /** Whether the entity was pushed out from above (landed on top of platform). */
  landedOnTop: boolean;
  /** Whether the entity hit the underside of the platform. */
  hitCeiling: boolean;
  /** Whether the entity hit the left face of the platform. */
  hitLeft: boolean;
  /** Whether the entity hit the right face of the platform. */
  hitRight: boolean;
}

/**
 * Test whether two AABBs overlap (strict — touching edges are NOT overlap).
 * @param a - First bounding box.
 * @param b - Second bounding box.
 * @returns True if the boxes overlap; false if they merely touch or are separate.
 */
export function aabbOverlaps(a: AABB, b: AABB): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/**
 * Reusable probe box for `hasFloorAhead`. Module-level to avoid per-tick
 * allocation in the enemy update path.
 */
const _floorProbe: AABB = { x: 0, y: 0, width: 2, height: 2 };

/**
 * Is there solid floor under the step an entity is about to take?
 *
 * Samples a 2×2 probe just past the leading edge, one pixel below the
 * entity's feet, and reports whether any solid platform is there. Ground
 * enemies call this to avoid walking into a pit; how they react is their
 * own business — patrollers reverse, chasers stop.
 *
 * DECISION: solid `platforms` only, never `oneWayPlatforms`. Ground enemies
 * resolve collision against the solid set alone, so a one-way tile is not
 * floor as far as they are concerned. Treating it as floor here would report
 * safe ground an enemy then falls straight through — exactly the bug that put
 * a Gearling into the void on levels 3-3 and 4-3 (see commit 74da6ad).
 *
 * @param x - Entity left edge.
 * @param y - Entity top edge.
 * @param width - Entity width.
 * @param height - Entity height.
 * @param vx - Horizontal velocity; its sign picks the leading edge.
 * @param platforms - Solid platforms to test against.
 * @returns True if floor is present ahead, false if the next step is over air.
 */
export function hasFloorAhead(
  x: number,
  y: number,
  width: number,
  height: number,
  vx: number,
  platforms: readonly AABB[],
): boolean {
  _floorProbe.x = vx > 0 ? x + width + 2 : x - 2;
  _floorProbe.y = y + height + 1;
  for (let i = 0; i < platforms.length; i++) {
    if (aabbOverlaps(_floorProbe, platforms[i])) return true;
  }
  return false;
}

/**
 * Compute the minimum overlap on each axis for two overlapping AABBs.
 * Returns zeroes when the boxes do not overlap.
 * @param a - First bounding box.
 * @param b - Second bounding box.
 * @returns `{ overlapX, overlapY }` — signed overlap values.
 */
function computeOverlap(
  a: AABB,
  b: AABB,
): { overlapX: number; overlapY: number } {
  if (!aabbOverlaps(a, b)) return { overlapX: 0, overlapY: 0 };

  const aRight = a.x + a.width;
  const bRight = b.x + b.width;
  const aBottom = a.y + a.height;
  const bBottom = b.y + b.height;

  // DECISION: compute signed overlap on each axis; use the smaller axis for
  // minimum-translation-vector resolution — standard AABB depenetration approach.
  const overlapLeft = bRight - a.x;
  const overlapRight = aRight - b.x;
  const overlapTop = bBottom - a.y;
  const overlapBottom = aBottom - b.y;

  const overlapX = overlapLeft < overlapRight ? overlapLeft : -overlapRight;
  const overlapY = overlapTop < overlapBottom ? overlapTop : -overlapBottom;

  return { overlapX, overlapY };
}

/**
 * Resolve a single entity hitbox against one platform AABB.
 * Applies ledge-forgiveness: if the entity's corner overlaps the platform by
 * ≤ LEDGE_FORGIVENESS pixels horizontally while landing, the entity is nudged
 * sideways to prevent "catching" on platform lips during running jumps.
 *
 * @param entityX  - Entity's current X (left edge of hitbox, world space).
 * @param entityY  - Entity's current Y (top edge of hitbox, world space).
 * @param hitbox   - Entity hitbox dimensions (width/height only — x/y ignored).
 * @param platform - Platform AABB in world space.
 * @param vy       - Entity's current vertical velocity. Retained for future
 *                   velocity-dependent effects (e.g. one-way platforms); not
 *                   currently used — landing direction is derived from MTV sign.
 * @returns CollisionResult with corrected position and collision flags, or null if no overlap.
 */
export function resolvePlatform(
  entityX: number,
  entityY: number,
  hitbox: AABB,
  platform: AABB,
  _vy: number,
): CollisionResult | null {
  const entityBox: AABB = {
    x: entityX,
    y: entityY,
    width: hitbox.width,
    height: hitbox.height,
  };

  if (!aabbOverlaps(entityBox, platform)) return null;

  const { overlapX, overlapY } = computeOverlap(entityBox, platform);

  let x = entityX;
  let y = entityY;
  let landedOnTop = false;
  let hitCeiling = false;
  let hitLeft = false;
  let hitRight = false;

  // DECISION: resolve on the axis of smaller penetration depth — avoids
  // corner-cases where a fast entity tunnels along one axis while being
  // resolved on the wrong one.
  // EXCEPTION: When the entity barely penetrates from above (within GROUND_SKIN),
  // always resolve vertically. This prevents adjacent same-height platforms from
  // creating invisible walls at their shared seam — at low horizontal speeds the
  // horizontal overlap can be smaller than the gravity-induced vertical overlap,
  // incorrectly triggering horizontal depenetration.
  const forceVerticalLanding = overlapY < 0 && Math.abs(overlapY) <= GROUND_SKIN;

  if (forceVerticalLanding || Math.abs(overlapY) < Math.abs(overlapX)) {
    // Vertical resolution
    // DECISION: negative overlapY = entity's bottom barely penetrated platform top
    // (came from above). Positive overlapY = entity's top barely penetrated platform
    // bottom (came from below). Sign derived from MTV computation above.
    if (overlapY < 0) {
      // Entity came from above — landed on top
      if (forceVerticalLanding) {
        // DECISION: Skip ledge forgiveness when GROUND_SKIN triggered. The entity
        // is walking across a flat seam between adjacent platforms, not landing on
        // a corner from a jump. Ledge forgiveness would incorrectly nudge the
        // entity backwards, creating an invisible wall at the seam.
        y = platform.y - hitbox.height;
        landedOnTop = true;
      } else {
        // Ledge forgiveness: nudge horizontally if corner barely clips the platform.
        const entityRight = entityX + hitbox.width;
        const clipLeft = platform.x - entityRight; // negative = overlap from right side
        const clipRight = entityX - (platform.x + platform.width); // negative = overlap from left side
        const absClipLeft = Math.abs(clipLeft);
        const absClipRight = Math.abs(clipRight);

        if (absClipLeft <= LEDGE_FORGIVENESS && absClipLeft < hitbox.width / 2) {
          // Corner barely clips left edge of platform — nudge entity left and land
          x = platform.x - hitbox.width;
          y = platform.y - hitbox.height;
          landedOnTop = true;
        } else if (
          absClipRight <= LEDGE_FORGIVENESS &&
          absClipRight < hitbox.width / 2
        ) {
          // Corner barely clips right edge — nudge entity right and land
          x = platform.x + platform.width;
          y = platform.y - hitbox.height;
          landedOnTop = true;
        } else {
          // Normal landing: push up
          y = platform.y - hitbox.height;
          landedOnTop = true;
        }
      }
    } else {
      // Entity came from below — hit ceiling
      y = platform.y + platform.height;
      hitCeiling = true;
    }
  } else {
    // DECISION: apply horizontal depenetration for all vertical velocities.
    // Prior guard (vy <= 0) skipped side hits while falling, causing corner
    // collisions to teleport Nova onto/under platforms instead of wall-blocking.
    if (overlapX > 0) {
      // Push right (entity hit left face of platform)
      x = platform.x + platform.width;
      hitLeft = true;
    } else {
      // Push left (entity hit right face of platform)
      x = platform.x - hitbox.width;
      hitRight = true;
    }
  }

  return { x, y, landedOnTop, hitCeiling, hitLeft, hitRight };
}

/**
 * Resolve an entity against all platforms in the level.
 * Iterates all platforms and applies corrections cumulatively.
 * Returns final position and combined collision flags.
 *
 * @param entityX   - Entity's X before resolution.
 * @param entityY   - Entity's Y before resolution.
 * @param hitbox    - Entity hitbox dimensions (width/height).
 * @param platforms - All solid platform AABBs in the level.
 * @param vy        - Entity's current vertical velocity (passed through to
 *                    `resolvePlatform`; not currently used for direction
 *                    determination — retained for future one-way platform support).
 * @returns Resolved position and aggregate collision flags.
 */
export function resolveAllPlatforms(
  entityX: number,
  entityY: number,
  hitbox: AABB,
  platforms: readonly AABB[],
  vy: number,
): CollisionResult {
  let x = entityX;
  let y = entityY;
  let landedOnTop = false;
  let hitCeiling = false;
  let hitLeft = false;
  let hitRight = false;

  for (const platform of platforms) {
    const result = resolvePlatform(x, y, hitbox, platform, vy);
    if (result === null) continue;
    x = result.x;
    y = result.y;
    if (result.landedOnTop) landedOnTop = true;
    if (result.hitCeiling) hitCeiling = true;
    if (result.hitLeft) hitLeft = true;
    if (result.hitRight) hitRight = true;
  }

  return { x, y, landedOnTop, hitCeiling, hitLeft, hitRight };
}
